<script>
import VSelect from './Common/VSelect';
import Loader from './Common/Loader';
import VInput from './Common/VInput';
import { sendForm } from './actions/StepForm';
import { mapActions } from 'vuex';

export default {
  components: {
    VSelect,
    Loader,
    VInput,
  },
  props: {
    title: {
      required: true,
      type: String,
    },
    steps: {
      required: true,
      type: () => [],
    },
    apiUrl: {
      required: true,
      type: String,
    },
    action: {
      type: String,
    },
  },
  data() {
    return {
      stepErrors: {},
      stepValues: {},
      currentStepIndex: 0,
      stepData: [],
      isRequestLoading: false,
      isRequestError: false,
    };
  },
  computed: {
    currentStep() {
      return this.steps[this.currentStepIndex];
    },
    showNextButton() {
      return this.currentStepIndex < this.steps.length - 1;
    },
  },
  watch: {
    currentStepIndex(value) {
      if (value === this.steps.length - 1) {
        this.sendFinishRequest();
      }
    },
  },
  created() {
    this.createStepValues(this.currentStepIndex);
  },
  methods: {
    ...mapActions({
      togglePopup: 'Popups/togglePopup',
      send: 'FormData/send',
    }),
    onNextStepButtonClick() {
      const errors = this.showErrors();

      if (Object.keys(errors).length) {
        return;
      }

      this.saveStepData();
      this.createStepValues(this.currentStepIndex + 1);
      this.currentStepIndex++;
    },
    showErrors() {
      this.stepErrors = {};

      this.currentStep.fields.forEach(field => {
        if (field.required && !this.stepValues[field.name]) {
          this.stepErrors[field.name] = 'Поле обязательное';
        }

        if (
          field.validationRule &&
          !field.validationRule.test(this.stepValues[field.name])
        ) {
          this.stepErrors[field.name] = 'Значение неверное';
        }
      });

      return this.stepErrors;
    },
    updateField(fieldName) {
      delete this.stepErrors[fieldName];
    },
    saveStepData() {
      this.stepData[this.currentStepIndex] = this.currentStep.fields.reduce(
        (acc, field) => {
          return {
            ...acc,
            [field.name]: {
              value: this.stepValues[field.name],
              kind: field.kind,
            },
          };
        },
        {},
      );
    },
    createStepValues(stepIndex) {
      this.stepValues = this.steps[stepIndex].fields.reduce(
        (acc, field) => ({ ...acc, [field.name]: null }),
        {},
      );
    },
    async sendFinishRequest() {
      const requestBody = this.createRequestBody();
      this.send(requestBody);
    },
    createRequestBody() {
      const formData = this.stepData.reduce((acc, cur) => {
        const stepValue = {};
        Object.entries(cur).forEach(([key, { value, kind }]) => {
          if (kind === 'select') {
            if (Array.isArray(value)) {
              stepValue[key] = value.map(({ id }) => id);
            } else {
              stepValue[key] = value?.id;
            }
          } else {
            stepValue[key] = value;
          }
        });
        return { ...acc, ...stepValue };
      }, {});

      if (this.action) {
        formData.action = this.action;
      }

      return formData;
    },
  },
};
</script>

<template>
  <div
    class="bg-white px-[20px] md:px-[32px] lg:px-[70px] py-[32px] md:py-[60px] rounded-[20px]"
  >
    <h2 class="title-h3 mb-[24px] md:mb-[40px]">{{ title }}</h2>
    <form novalidate>
      <div>
        <div class="flex mb-[40px] md:mb-[80px] lg:mb-[114px]">
          <div
            v-for="(step, index) in steps"
            :key="step.id"
            class="w-1/4 step"
            :class="{ 'step--active': currentStepIndex === index }"
          >
            <div
              class="flex items-center justify-center w-[60px] h-[60px] rounded-[60px] border mb-[16px] relative"
              :class="{
                'text-gray-500': currentStepIndex < index,
                'border-green bg-green text-white': currentStepIndex === index,
                'border-[#F5FAEF] bg-[#F5FAEF]': currentStepIndex > index,
              }"
            >
              <img
                v-if="currentStepIndex > index"
                src="/public/images/check.svg"
                :alt="step.title"
              />
              <template v-else>
                {{ index + 1 }}
              </template>
            </div>
            <div
              class="text-base"
              :class="{ 'text-gray-500': currentStepIndex !== index }"
            >
              {{ step.title }}
            </div>
          </div>
        </div>

        <div
          v-if="currentStep.fields.length"
          class="flex flex-col flex-wrap md:flex-row gap-[20px]"
        >
          <div
            v-for="field in currentStep.fields"
            :key="field.name"
            class="relative h-full md:w-[calc(33.33334%-13.33334px)]"
          >
            <template v-if="field.kind === 'input'">
              <v-input
                v-model="stepValues[field.name]"
                :key="`${field.id}/${field.name}`"
                :placeholder="field.placeholder"
                :theme="field.theme || 'gray'"
                :format="field.format"
                :error="stepErrors[field.name]"
                :name="field.name"
                :type="field.type"
                :required="field.required"
                @update:modelValue="updateField(field.name)"
              />
            </template>
            <template v-else-if="field.kind === 'select'">
              <v-select
                v-model="stepValues[field.name]"
                :options="field.options"
                :placeholder="field.placeholder"
                :error="stepErrors[field.name]"
                @update:modelValue="updateField(field.name)"
              />
            </template>
          </div>
        </div>

        <div v-if="showNextButton" class="text-center mt-[114px]">
          <button
            class="button button_green button_big w-[140px] inline-flex gap-[10px] items-center justify-center"
            @click.prevent="onNextStepButtonClick"
          >
            Далее <img src="/public/images/next-step.svg" alt="" />
          </button>
        </div>

        <div
          v-if="currentStepIndex === steps.length - 1"
          class="text-center relative"
        >
          <loader v-if="isRequestLoading" />
          <div v-else-if="isRequestError" class="title-h3">
            Произошла ошибка, попробуйте еще
          </div>
          <div v-else class="title-h3">Ваша заявка отправлена!</div>
        </div>
      </div>
    </form>
  </div>
</template>
